import { useEffect, useState, useMemo, ReactNode, ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Upload, Trash2, Image as ImageIcon, Video } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, ApiClientError } from '@/api/client';
import type { MediaItem, WorkflowFormData } from '@/types';

const schema = z.object({
  workflow_id: z
    .string()
    .min(1, 'Required')
    .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase letters, numbers, and underscores only'),
  title: z.string().min(1, 'Required'),
  description: z.string().optional(),
  permission: z.string().min(1, 'Required (e.g. patient.create)'),
  action_url: z.string().optional(),
  steps_text: z.string().min(1, 'At least one step required'),
  keywords_text: z.string().min(1, 'At least one keyword required'),
  related_text: z.string().optional(),
  status: z.enum(['draft', 'published', 'archived']),
});

type FormValues = z.infer<typeof schema>;

export function WorkflowEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      workflow_id: '',
      title: '',
      description: '',
      permission: '',
      action_url: '',
      steps_text: '',
      keywords_text: '',
      related_text: '',
      status: 'draft',
    },
  });

  // Live-parse the steps textarea so the media editor can offer a per-step dropdown.
  const stepsText = useWatch({ control, name: 'steps_text' });
  const stepLabels = useMemo(() => {
    return (stepsText ?? '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
  }, [stepsText]);

  useEffect(() => {
    if (isNew) return;
    api
      .getWorkflow(Number(id))
      .then((w) => {
        reset({
          workflow_id: w.workflow_id,
          title: w.title,
          description: w.description ?? '',
          permission: w.permission,
          action_url: w.action_url ?? '',
          steps_text: w.steps.join('\n'),
          keywords_text: w.keywords.join(', '),
          related_text: (w.related_workflows ?? []).join(', '),
          status: w.status,
        });
        setMedia(w.media ?? []);
        setLoading(false);
      })
      .catch((e) => {
        const msg = e instanceof ApiClientError ? `${e.code}: ${e.message}` : (e as Error).message;
        setSubmitError(msg);
        setLoading(false);
      });
  }, [id, isNew, reset]);

  async function handleFileSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;

    setUploadError(null);
    setUploading(true);
    try {
      const result = await api.uploadFile(file);
      setMedia((prev) => [
        ...prev,
        { url: result.url, type: result.type, caption: '' },
      ]);
    } catch (err) {
      const msg = err instanceof ApiClientError ? `${err.code}: ${err.message}` : (err as Error).message;
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  }

  function updateMediaItem(index: number, patch: Partial<MediaItem>) {
    setMedia((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  }

  function removeMediaItem(index: number) {
    setMedia((prev) => prev.filter((_, i) => i !== index));
  }

  async function onSubmit(values: FormValues) {
    setSubmitError(null);

    const payload: WorkflowFormData = {
      workflow_id: values.workflow_id,
      title: values.title,
      description: values.description?.trim() || null,
      permission: values.permission,
      action_url: values.action_url?.trim() || null,
      steps: values.steps_text
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
      keywords: values.keywords_text
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      related_workflows: values.related_text
        ? values.related_text
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      media: media.map((m) => ({
        url: m.url,
        type: m.type,
        ...(m.caption ? { caption: m.caption } : {}),
        ...(m.step_index !== undefined && m.step_index !== null
          ? { step_index: m.step_index }
          : {}),
      })),
      status: values.status,
    };

    try {
      if (isNew) {
        const created = await api.createWorkflow(payload);
        navigate(`/workflows/${created.id}`);
      } else {
        await api.updateWorkflow(Number(id), payload);
        navigate('/workflows');
      }
    } catch (e) {
      const msg = e instanceof ApiClientError ? `${e.code}: ${e.message}` : (e as Error).message;
      setSubmitError(msg);
    }
  }

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;

  return (
    <div className="max-w-3xl">
      <button
        onClick={() => navigate('/workflows')}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={16} />
        Back to workflows
      </button>

      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
        {isNew ? 'Create workflow' : 'Edit workflow'}
      </h2>
      <p className="text-gray-500 mb-8">
        Define how the AI agent should respond to a particular user task.
      </p>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="bg-white rounded-xl border border-gray-200 p-6 space-y-5"
      >
        <div className="grid grid-cols-2 gap-5">
          <Field
            label="Workflow ID"
            error={errors.workflow_id?.message}
            hint={isNew ? 'Lowercase, e.g. register_patient' : "Stable identifier — can't be changed after creation"}
          >
            <input
              {...register('workflow_id')}
              className="input font-mono"
              placeholder="register_patient"
              readOnly={!isNew}
              disabled={!isNew}
            />
          </Field>
          <Field label="Status" error={errors.status?.message}>
            <select {...register('status')} className="input">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </Field>
        </div>

        <Field label="Title" error={errors.title?.message}>
          <input {...register('title')} className="input" placeholder="Register a New Patient" />
        </Field>

        <Field
          label="Description"
          error={errors.description?.message}
          hint="Optional. Shown to admins, not to the agent."
        >
          <textarea
            {...register('description')}
            className="input h-20"
            placeholder="Add a new patient to ProMed…"
          />
        </Field>

        <div className="grid grid-cols-2 gap-5">
          <Field
            label="Permission"
            error={errors.permission?.message}
            hint="Must match a ProMed RBAC permission"
          >
            <input {...register('permission')} className="input font-mono" placeholder="patient.create" />
          </Field>
          <Field
            label="Action URL"
            error={errors.action_url?.message}
            hint="Optional. Where to navigate when the user wants to do this"
          >
            <input {...register('action_url')} className="input font-mono" placeholder="/Patient/Add/" />
          </Field>
        </div>

        <Field
          label="Steps"
          error={errors.steps_text?.message}
          hint="One step per line. The agent will repeat these to the user."
        >
          <textarea
            {...register('steps_text')}
            className="input h-32 font-mono text-xs"
            placeholder={"Click the 'Registration' menu\nSelect 'Registration' from the dropdown\nFill in patient details — fields with * are mandatory\nClick 'Save'"}
          />
        </Field>

        <Field
          label="Keywords"
          error={errors.keywords_text?.message}
          hint="Comma-separated. Used by the LLM to match user intent."
        >
          <input
            {...register('keywords_text')}
            className="input"
            placeholder="register patient, new patient, add patient"
          />
        </Field>

        <Field
          label="Related workflows"
          error={errors.related_text?.message}
          hint="Comma-separated workflow_ids (optional)"
        >
          <input
            {...register('related_text')}
            className="input font-mono"
            placeholder="edit_patient, view_patient"
          />
        </Field>

        <div>
          <label className="label">Media</label>
          <p className="text-xs text-gray-500 mb-3">
            Optional images or videos that help users understand the steps. Saved on the workflow and shown by the agent when relevant. Max 5 MB images / 25 MB video.
          </p>

          {media.length > 0 && (
            <div className="space-y-3 mb-3">
              {media.map((item, i) => (
                <MediaRow
                  key={`${item.url}-${i}`}
                  item={item}
                  stepLabels={stepLabels}
                  onChange={(patch) => updateMediaItem(i, patch)}
                  onRemove={() => removeMediaItem(i)}
                />
              ))}
            </div>
          )}

          <label className="btn-secondary cursor-pointer inline-flex">
            <Upload size={15} className="mr-2" />
            {uploading ? 'Uploading…' : 'Add image or video'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm"
              className="hidden"
              onChange={handleFileSelected}
              disabled={uploading}
            />
          </label>

          {uploadError && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              {uploadError}
            </div>
          )}
        </div>

        {submitError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {submitError}
          </div>
        )}

        <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            <Save size={16} className="mr-2" />
            {isSubmitting ? 'Saving…' : isNew ? 'Create workflow' : 'Save changes'}
          </button>
          <button type="button" onClick={() => navigate('/workflows')} className="btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function MediaRow({
  item,
  stepLabels,
  onChange,
  onRemove,
}: {
  item: MediaItem;
  stepLabels: string[];
  onChange: (patch: Partial<MediaItem>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
      <div className="flex-shrink-0">
        {item.type === 'image' ? (
          <img
            src={item.url}
            alt={item.caption ?? ''}
            className="w-24 h-24 object-cover rounded border border-gray-200 bg-white"
          />
        ) : (
          <video
            src={item.url}
            className="w-24 h-24 object-cover rounded border border-gray-200 bg-black"
            muted
            playsInline
          />
        )}
        <div className="flex items-center justify-center gap-1 mt-1 text-[10px] text-gray-500">
          {item.type === 'image' ? <ImageIcon size={11} /> : <Video size={11} />}
          {item.type}
        </div>
      </div>

      <div className="flex-1 min-w-0 space-y-2">
        <input
          type="text"
          className="input text-sm"
          placeholder="Caption (optional)"
          value={item.caption ?? ''}
          onChange={(e) => onChange({ caption: e.target.value })}
        />
        <select
          className="input text-sm"
          value={item.step_index ?? ''}
          onChange={(e) =>
            onChange({
              step_index: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        >
          <option value="">Workflow-level (no specific step)</option>
          {stepLabels.map((label, i) => (
            <option key={i} value={i}>
              Step {i + 1}: {label.length > 40 ? label.slice(0, 40) + '…' : label}
            </option>
          ))}
        </select>
        <div className="text-[11px] font-mono text-gray-500 truncate" title={item.url}>
          {item.url}
        </div>
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 self-start text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
        title="Remove"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
}
